import { useEffect, useMemo, useState } from 'react'

// MWA-254 Defect 2 — shared client-side pagination for Job Search list tables.
export const ROWS_PER_PAGE_OPTIONS = [10, 25, 50]
export const DEFAULT_ROWS_PER_PAGE = 25

export interface TablePaginationController<T> {
  /** The slice of rows for the current page — render these instead of the full list. */
  paginated: T[]
  page: number
  rowsPerPage: number
  count: number
  setPage: (page: number) => void
  setRowsPerPage: (rowsPerPage: number) => void
}

/**
 * Client-side pagination for list tables (MWA-254 Defect 2).
 *
 * Pass the already-filtered/sorted rows. `resetKey` should encode anything that
 * changes the result set (active tab, filter values, sort column/order) so the
 * view jumps back to page 1 when it changes — otherwise you can land on an
 * out-of-range page. The current page is also clamped if the row count shrinks
 * below the current window, so the table never renders an empty page.
 */
export function useTablePagination<T>(
  rows: T[],
  resetKey?: unknown
): TablePaginationController<T> {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(DEFAULT_ROWS_PER_PAGE)

  useEffect(() => {
    setPage(0)
  }, [resetKey])

  const pageCount = Math.max(1, Math.ceil(rows.length / rowsPerPage))
  const currentPage = Math.min(page, pageCount - 1)

  const paginated = useMemo(
    () => rows.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage),
    [rows, currentPage, rowsPerPage]
  )

  return {
    paginated,
    page: currentPage,
    rowsPerPage,
    count: rows.length,
    setPage,
    setRowsPerPage,
  }
}
